import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Screen,
  ScrollRegion,
  Text,
  durations,
  easings,
} from "@andrum/ui";
import type { Experiment, ExperimentCategory } from "@andrum/schemas";
import type { WellbeingCategory } from "@andrum/design-tokens";
import { listPublishedExperiments } from "@/features/experiments/api/experiments-api";
import { useExperimentStore } from "@/stores/experiment-store";
import { hapticService } from "@/lib/capacitor";
import { canUsePremiumExperiment } from "@/lib/billing/premium-gate";
import { PremiumGateCard } from "@/components/premium/PremiumGateCard";
import { cn } from "@/lib/utils";

const FILTER_IDS: Array<"all" | ExperimentCategory> = [
  "all",
  "recovery",
  "presence",
  "connection",
  "movement",
  "gratitude",
  "meaning",
];

function isCategory(value: string): value is WellbeingCategory {
  return (
    value === "connection" ||
    value === "movement" ||
    value === "recovery" ||
    value === "gratitude" ||
    value === "meaning" ||
    value === "presence"
  );
}

function ExperimentCard({
  exp,
  expanded,
  locked,
  busy,
  onToggle,
  onStart,
  onUnlock,
}: {
  exp: Experiment;
  expanded: boolean;
  locked: boolean;
  busy: boolean;
  onToggle: () => void;
  onStart: () => void;
  onUnlock: () => void;
}) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  return (
    <Card
      padding="none"
      elevation={expanded ? "floating" : "raised"}
      className={cn(
        "overflow-hidden transition-shadow",
        expanded && "ring-1 ring-primary/20"
      )}
    >
      <button
        type="button"
        onClick={() => {
          void hapticService.selection();
          onToggle();
        }}
        className="w-full cursor-pointer px-5 pt-5 pb-4 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              {isCategory(exp.category) ? (
                <Badge category={exp.category} />
              ) : null}
              {exp.premium ? (
                <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-[11px] font-semibold text-primary">
                  {t("plus")}
                </span>
              ) : null}
              <span className="text-xs tabular-nums text-foreground-subtle">
                {t("explore.meta", {
                  minutes: exp.durationMinutes,
                  level: exp.difficulty,
                })}
              </span>
            </div>
            <h3 className="font-display text-lg font-semibold leading-snug tracking-tight text-foreground">
              {exp.title}
            </h3>
            {!expanded ? (
              <p className="text-[15px] leading-relaxed text-foreground-muted">
                {exp.summary}
              </p>
            ) : null}
          </div>
          <span
            className={cn(
              "mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-sm font-medium text-foreground-muted transition-transform",
              expanded && "rotate-180 bg-primary-soft text-primary"
            )}
            aria-hidden
          >
            ⌄
          </span>
        </div>
        {!expanded ? (
          <p className="mt-3 text-xs font-medium text-primary">
            {t("explore.tapToExpand")}
          </p>
        ) : null}
      </button>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            key="body"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 1 } : { height: 0, opacity: 0 }}
            transition={{ duration: durations.normal, ease: easings.standard }}
            className="overflow-hidden"
          >
            <div className="space-y-4 border-t border-border/60 px-5 pb-5 pt-4">
              <div className="space-y-1.5">
                <Text variant="label">{t("explore.summary")}</Text>
                <p className="text-[15px] leading-relaxed text-foreground-muted">
                  {exp.summary}
                </p>
              </div>

              {locked ? (
                <>
                  <div className="relative overflow-hidden rounded-2xl border border-primary/15 bg-primary-soft/25 px-3.5 py-4">
                    <p className="text-sm leading-relaxed text-foreground-muted">
                      {t("explore.premiumTeaser", {
                        minutes: exp.durationMinutes,
                        steps: exp.instructions?.length ?? 0,
                      })}
                    </p>
                    <p className="mt-2 text-xs leading-snug text-foreground-subtle">
                      {t("explore.premiumLockedDetail")}
                    </p>
                  </div>
                  <Button fullWidth variant="soft" onClick={onUnlock}>
                    {t("explore.unlockPlus")}
                  </Button>
                </>
              ) : (
                <>
                  {exp.rationale ? (
                    <div className="space-y-1.5 rounded-2xl bg-primary-soft/35 px-3.5 py-3">
                      <Text variant="label">{t("explore.why")}</Text>
                      <p className="text-sm leading-relaxed text-foreground-muted">
                        {exp.rationale}
                      </p>
                    </div>
                  ) : null}

                  {exp.instructions?.length ? (
                    <div className="space-y-2">
                      <Text variant="label">{t("explore.howTo")}</Text>
                      <ol className="list-decimal space-y-2 pl-4 text-[15px] leading-relaxed text-foreground">
                        {exp.instructions.map((step, i) => (
                          <li key={i} className="pl-1">
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}

                  {exp.smallerVersion ? (
                    <div className="space-y-1 rounded-2xl border border-border/70 bg-surface-muted/50 px-3.5 py-3">
                      <Text variant="label">{t("explore.smaller")}</Text>
                      <p className="text-sm leading-relaxed text-foreground-muted">
                        {exp.smallerVersion}
                      </p>
                    </div>
                  ) : null}

                  <Button
                    fullWidth
                    variant="secondary"
                    loading={busy}
                    onClick={onStart}
                  >
                    {t("explore.doThis")}
                  </Button>
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  void hapticService.selection();
                  onToggle();
                }}
                className="w-full py-1 text-center text-sm font-medium text-foreground-muted"
              >
                {t("explore.collapse")}
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {!expanded ? (
        <div className="border-t border-border/50 px-5 py-3">
          {locked ? (
            <Button fullWidth variant="soft" className="min-h-11" onClick={onUnlock}>
              {t("explore.unlockPlus")}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="secondary"
              className="min-h-11"
              loading={busy}
              onClick={onStart}
            >
              {t("explore.doThis")}
            </Button>
          )}
        </div>
      ) : null}
    </Card>
  );
}

export function ExploreScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assignFromCatalog = useExperimentStore((s) => s.assignFromCatalog);
  const storeExperiments = useExperimentStore((s) => s.experiments);
  const storeHydrated = useExperimentStore((s) => s.hydrated);
  const [experiments, setExperiments] = useState<Experiment[]>(() =>
    storeExperiments.length > 0 ? storeExperiments : []
  );
  const [filter, setFilter] = useState<"all" | ExperimentCategory>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(
    () => !(storeHydrated && storeExperiments.length > 0)
  );
  const [hasPremium, setHasPremium] = useState(false);

  const filters = useMemo(
    () =>
      FILTER_IDS.map((id) => ({
        id,
        label:
          id === "all" ? t("explore.all") : t(`explore.categories.${id}`),
      })),
    [t]
  );

  const refresh = async (opts?: { soft?: boolean }) => {
    if (!opts?.soft) setLoading(true);
    setLoadError(null);
    try {
      const [list, premium] = await Promise.all([
        listPublishedExperiments(),
        canUsePremiumExperiment(),
      ]);
      setExperiments(list);
      setHasPremium(premium);
    } catch {
      if (experiments.length === 0) {
        setLoadError(t("explore.loadError"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ soft: storeHydrated && storeExperiments.length > 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const filtered = useMemo(() => {
    const byCat =
      filter === "all"
        ? experiments
        : experiments.filter((e) => e.category === filter);
    // Free users only see free steps in the main list — Plus is upsell, not a catalog browse.
    if (!hasPremium) return byCat.filter((e) => !e.premium);
    return byCat;
  }, [experiments, filter, hasPremium]);

  const premiumCount = useMemo(
    () => experiments.filter((e) => e.premium).length,
    [experiments]
  );

  useEffect(() => {
    setExpandedId(null);
  }, [filter]);

  const handleStart = async (exp: Experiment) => {
    setError(null);
    if (exp.premium && !hasPremium) {
      void navigate({
        to: "/app/paywall",
        search: { reason: "explore" },
      });
      return;
    }
    setBusyId(exp.id);
    void hapticService.selection();
    const assignment = await assignFromCatalog(exp.id);
    setBusyId(null);
    if (!assignment) {
      setError(t("explore.startError"));
      return;
    }
    void navigate({
      to: "/experiment/$assignmentId",
      params: { assignmentId: assignment.id },
    });
  };

  if (loading) {
    return <Screen loading skeleton loadingLabel={t("loadingShort")} />;
  }

  if (loadError) {
    return (
      <Screen>
        <EmptyState
          title={t("explore.loadErrorTitle")}
          description={loadError}
          action={
            <Button fullWidth onClick={() => void refresh()}>
              {t("retry")}
            </Button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen scrollMode="lock" className="space-y-3">
      <header className="shrink-0 space-y-1">
        <Text variant="title" as="h1">
          {t("explore.title")}
        </Text>
        <Text variant="muted" className="text-sm leading-snug">
          {t("explore.lead")}
        </Text>
      </header>

      <div className="flex shrink-0 gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => {
              void hapticService.selection();
              setFilter(f.id);
            }}
            className={cn(
              "shrink-0 rounded-full px-3.5 py-2 text-xs font-medium transition-colors",
              filter === f.id
                ? "bg-primary text-primary-foreground"
                : "bg-surface-muted text-foreground-muted"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error ? (
        <Text variant="caption" className="shrink-0 text-danger">
          {error}
        </Text>
      ) : null}

      <ScrollRegion className="space-y-3 pb-2">
        {filtered.length === 0 ? (
          <EmptyState
            title={t("explore.emptyTitle")}
            description={
              experiments.length === 0
                ? t("explore.emptyCatalog")
                : t("explore.emptyFilter")
            }
            action={
              filter !== "all" ? (
                <Button fullWidth variant="soft" onClick={() => setFilter("all")}>
                  {t("explore.showAll")}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="soft"
                  onClick={() => void navigate({ to: "/app/today" })}
                >
                  {t("explore.toToday")}
                </Button>
              )
            }
          />
        ) : (
          filtered.map((exp) => (
            <ExperimentCard
              key={exp.id}
              exp={exp}
              expanded={expandedId === exp.id}
              locked={Boolean(exp.premium && !hasPremium)}
              busy={busyId === exp.id}
              onToggle={() =>
                setExpandedId((id) => (id === exp.id ? null : exp.id))
              }
              onStart={() => void handleStart(exp)}
              onUnlock={() =>
                void navigate({
                  to: "/app/paywall",
                  search: { reason: "explore" },
                })
              }
            />
          ))
        )}

        {!hasPremium && premiumCount > 0 ? (
          <div className="pt-1">
            <PremiumGateCard
              compact
              reason="explore"
              title={t("explore.plusCardTitle")}
              description={t("explore.plusCardBodyRestricted", {
                count: premiumCount,
              })}
            />
          </div>
        ) : null}
      </ScrollRegion>
    </Screen>
  );
}
